import { Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { EEOActions, EEOCategoriesSelectors } from '@recruitee/eeo-data';
import { I18nService } from '@recruitee/i18n';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

import {
  ManageEeoDialogComponent,
  ManageEeoDialogResult,
} from './manage-eeo-dialog/manage-eeo-dialog.component';

@Injectable()
export class ManageEeoBulkService {
  private readonly confirmDialogTexts = {
    enable: {
      title: this.i18n.translate('app.offers.manage_eeo.confirm_enable.title'),
      message: this.i18n.translate('app.offers.manage_eeo.confirm_enable.message'),
    },
    disable: {
      title: this.i18n.translate('app.offers.manage_eeo.confirm_disable.title'),
      message: this.i18n.translate('app.offers.manage_eeo.confirm_disable.message'),
    },
  };

  private jobCategories: string[] = [];
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialog: DialogService,
    private store: Store,
    private i18n: I18nService,
    private actions$: Actions,
  ) {
    store
      .select(EEOCategoriesSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(data => (this.jobCategories = data.jobCategories));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public manageEeo$({ ids }: { ids: number[] }): Observable<boolean> {
    return this.openManageDialog$({ ids }).pipe(
      switchMap(result => {
        if (!result) return of(null);
        return this.confirm$({ enable: result.action === 'enable', jobsCount: ids.length }).pipe(
          map(confirmResult => {
            if (!confirmResult) return null;
            return result;
          }),
        );
      }),
      tap(result => {
        if (!result) return;
        if (result.action === 'enable')
          this.store.dispatch(
            EEOActions.bulkEnable({
              payload: { offerIds: ids, jobCategory: result.jobCategory! },
            }),
          );
        else
          this.store.dispatch(
            EEOActions.bulkDisable({
              payload: { offerIds: ids },
            }),
          );
      }),
      switchMap(result => of(!!result)),
    );
  }

  private openManageDialog$({ ids }: { ids: number[] }): Observable<ManageEeoDialogResult> {
    return this.dialog.openCustom(
      ManageEeoDialogComponent,
      {
        width: DialogWidth.Small,
      },
      {
        jobCategories: this.jobCategories,
        jobsCount: ids.length,
      },
    ).afterClosed;
  }

  private confirm$(props: { enable: boolean; jobsCount: number }): Observable<boolean> {
    const { enable, jobsCount } = props;
    const texts = enable ? this.confirmDialogTexts.enable : this.confirmDialogTexts.disable;
    const confirmConfig = {
      ...texts,
      agreeLabel: this.i18n.translate(
        enable
          ? 'app.offers.manage_eeo.confirm_enable.agree_label'
          : 'app.offers.manage_eeo.confirm_disable.agree_label',
        { count: jobsCount },
      ),
    };
    return enable ? this.dialog.information(confirmConfig) : this.dialog.warning(confirmConfig);
  }
}
